import { LegalEntityFieldGrouping } from "@common/components/filterPopover/FilterPopover.types";
import { LegalEntityColumnId } from "./legalEntityColumns";
import { customFieldFilterIdPrefix } from "@common/components/filterPopover/customFieldFilterOption";

// Which bucket each filter and each column belongs to in the filter menu and the column picker. One table rather
// than a grouping on every entry, so the whole layout is readable in one place and the two pickers cannot drift
// apart. Anything not named here is a field of the entity itself, which is the largest group by far.
//
// The filter bar and the column picker speak different id vocabularies for the same field - "tia-not-necessary"
// against "tiaNotNecessary" - so each gets its own table.

const filterGroupings: Record<string, LegalEntityFieldGrouping> = {
    "classification-questionnaire-ids": LegalEntityFieldGrouping.VendorManagement,
    "classification-risk-levels": LegalEntityFieldGrouping.VendorManagement,
    areas: LegalEntityFieldGrouping.VendorManagement,
    "latest-audit-questionnaire-ids": LegalEntityFieldGrouping.VendorManagement,
    "latest-audit-statuses": LegalEntityFieldGrouping.VendorManagement,
    certifications: LegalEntityFieldGrouping.VendorManagement,
    "supervision-types": LegalEntityFieldGrouping.VendorManagement,
    "supervision-frequencies": LegalEntityFieldGrouping.VendorManagement,

    locations: LegalEntityFieldGrouping.Transfers,
    "tia-not-necessary": LegalEntityFieldGrouping.Transfers,
    "transfer-basis-not-necessary": LegalEntityFieldGrouping.Transfers,
    "transfer-basis-ids": LegalEntityFieldGrouping.Transfers,
    "sub-contractor-ids": LegalEntityFieldGrouping.Transfers,
    "sender-on-source": LegalEntityFieldGrouping.Transfers,
    "sender-on-data-processor": LegalEntityFieldGrouping.Transfers,
    "data-processor": LegalEntityFieldGrouping.Transfers,
    "sub-processor": LegalEntityFieldGrouping.Transfers,
    "recipient-on-disclosure": LegalEntityFieldGrouping.Transfers,
    "has-sharings": LegalEntityFieldGrouping.Transfers,
    roles: LegalEntityFieldGrouping.Transfers,

    "has-documents": LegalEntityFieldGrouping.Documents,
    "document-type-ids": LegalEntityFieldGrouping.Documents,
    "document-owner-ids": LegalEntityFieldGrouping.Documents,
    "document-group-entity-ids": LegalEntityFieldGrouping.Documents,

    "has-processing-activities": LegalEntityFieldGrouping.ProcessingActivities,
    "data-categories": LegalEntityFieldGrouping.ProcessingActivities,
    "has-data-categories": LegalEntityFieldGrouping.ProcessingActivities,
    "data-subjects": LegalEntityFieldGrouping.ProcessingActivities,
    "has-data-subjects": LegalEntityFieldGrouping.ProcessingActivities,

    "risk-area-levels": LegalEntityFieldGrouping.Risk,
};

const columnGroupings: Record<string, LegalEntityFieldGrouping> = {
    [LegalEntityColumnId.complianceAreas]: LegalEntityFieldGrouping.VendorManagement,
    [LegalEntityColumnId.classifications]: LegalEntityFieldGrouping.VendorManagement,
    [LegalEntityColumnId.certifications]: LegalEntityFieldGrouping.VendorManagement,
    [LegalEntityColumnId.latestAudit]: LegalEntityFieldGrouping.VendorManagement,
    [LegalEntityColumnId.latestAuditStatus]: LegalEntityFieldGrouping.VendorManagement,
    [LegalEntityColumnId.supervisionType]: LegalEntityFieldGrouping.VendorManagement,
    [LegalEntityColumnId.supervisionFrequency]: LegalEntityFieldGrouping.VendorManagement,

    [LegalEntityColumnId.location]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.transferBasis]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.transferBasisNotNecessary]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.tiaNotNecessary]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.subContractors]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.dataProcessor]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.subProcessor]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.recipientOnDisclosure]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.senderOnSource]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.senderOnDataProcessor]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.roles]: LegalEntityFieldGrouping.Transfers,
    [LegalEntityColumnId.numberOfSharings]: LegalEntityFieldGrouping.Transfers,

    [LegalEntityColumnId.documents]: LegalEntityFieldGrouping.Documents,
    [LegalEntityColumnId.hasDocuments]: LegalEntityFieldGrouping.Documents,
    [LegalEntityColumnId.documentTypes]: LegalEntityFieldGrouping.Documents,
    [LegalEntityColumnId.documentOwners]: LegalEntityFieldGrouping.Documents,
    [LegalEntityColumnId.documentGroupEntities]: LegalEntityFieldGrouping.Documents,

    [LegalEntityColumnId.processingActivities]: LegalEntityFieldGrouping.ProcessingActivities,
    [LegalEntityColumnId.numberOfProcessingActivities]: LegalEntityFieldGrouping.ProcessingActivities,
    [LegalEntityColumnId.dataCategories]: LegalEntityFieldGrouping.ProcessingActivities,
    [LegalEntityColumnId.numberOfDataCategories]: LegalEntityFieldGrouping.ProcessingActivities,
    [LegalEntityColumnId.dataSubjects]: LegalEntityFieldGrouping.ProcessingActivities,
    [LegalEntityColumnId.numberOfDataSubjects]: LegalEntityFieldGrouping.ProcessingActivities,

    [LegalEntityColumnId.riskAreas]: LegalEntityFieldGrouping.Risk,
};

export function legalEntityFilterGrouping(filterId: string): LegalEntityFieldGrouping {
    if (filterId.startsWith(customFieldFilterIdPrefix)) {
        return LegalEntityFieldGrouping.CustomFields;
    }

    return filterGroupings[filterId] ?? LegalEntityFieldGrouping.LegalEntity;
}

export function legalEntityColumnGrouping(columnId: string): LegalEntityFieldGrouping {
    return columnGroupings[columnId] ?? LegalEntityFieldGrouping.LegalEntity;
}
